import { Org, OrgAddressBase } from 'types/org';
import { TeamLite } from 'types/properties';
import {
  StockLocationType,
  PackagingOptions,
  PrinterLabelSize,
} from 'types/inventory';
import { DateTimeString } from 'types';
import { Lite, PATCHSchema, POSTSchema } from 'types/api';
import { PartRuleSet } from 'types/partRules';
import { MANAGED_INVENTORY_ROUTES } from 'routes/constants';
import { ShipmentSchema, ShipmentSchemaLite } from 'types/clientV2/shipments';
import { KitRequestSchema, KitRequestSchemaLite } from 'types/kitting';
import {
  IconArchive,
  IconTallymarks,
  IconPlaneDeparture,
  IconCut,
  IconArrowsJoin,
  IconDisc,
  IconDropletOff,
  IconTruckDelivery,
  IconMicroscope,
  IconBasket,
  IconArrowUp,
  IconAlertTriangle,
} from '@tabler/icons-react';

export enum StockLocationPurpose {
  STORAGE = 'storage',
  COUNTING = 'counting',
  SHIPPING = 'shipping',
  SPLITTING = 'splitting',
  SPLICING = 'splicing',
  REELING = 'reeling',
  BAKING = 'baking',
  RECEIVING = 'receiving',
  INSPECTION = 'inspection',
  PICKING = 'picking',
  PUTTING = 'putting',
  QUARANTINE = 'quarantine',
}

export const stockLocationPurposeLabels = {
  [StockLocationPurpose.STORAGE]: 'Storage',
  [StockLocationPurpose.COUNTING]: 'Counting',
  [StockLocationPurpose.SHIPPING]: 'Shipping',
  [StockLocationPurpose.SPLITTING]: 'Splitting',
  [StockLocationPurpose.SPLICING]: 'Splicing',
  [StockLocationPurpose.REELING]: 'Reeling',
  [StockLocationPurpose.BAKING]: 'Baking',
  [StockLocationPurpose.RECEIVING]: 'Receiving',
  [StockLocationPurpose.INSPECTION]: 'Inspection',
  [StockLocationPurpose.PICKING]: 'Picking',
  [StockLocationPurpose.PUTTING]: 'Putting',
  [StockLocationPurpose.QUARANTINE]: 'Quarantine',
};

export enum WorkstationType {
  PRE_RECEIVING = 'pre_receiving',
  INLINE_RECEIVING = 'inline_receiving',
  FIXED_RECEIVING = 'fixed_receiving',
  MANUAL_RECEIVING = 'manual_receiving',
  FIXED_PICKING = 'fixed_picking',
  MOBILE_PICKING = 'mobile_picking',
  HANDLING = 'handling',
  SHIPPING = 'shipping',
  PUTTING = 'putting',
  LEGACY = 'legacy',
  COUNTING = 'counting',
  BAKING = 'baking',
  INSPECTION = 'inspection',
}

export const workstationTypeLabels = {
  [WorkstationType.PRE_RECEIVING]: 'Pre-Receiving',
  [WorkstationType.INLINE_RECEIVING]: 'Inline Receiving',
  [WorkstationType.FIXED_RECEIVING]: 'Fixed Receiving',
  [WorkstationType.MANUAL_RECEIVING]: 'Manual Receiving',
  [WorkstationType.FIXED_PICKING]: 'Fixed Picking',
  [WorkstationType.MOBILE_PICKING]: 'Mobile Picking',
  [WorkstationType.HANDLING]: 'Handling',
  [WorkstationType.SHIPPING]: 'Shipping',
  [WorkstationType.PUTTING]: 'Putting',
  [WorkstationType.LEGACY]: 'Legacy',
  [WorkstationType.COUNTING]: 'Counting',
  [WorkstationType.BAKING]: 'Baking',
  [WorkstationType.INSPECTION]: 'Inspection',
};

export const allowedWorkstationTypePairs = [
  {
    workstationType: WorkstationType.PRE_RECEIVING,
    locationPurpose: StockLocationPurpose.RECEIVING,
  },
  {
    workstationType: WorkstationType.INLINE_RECEIVING,
    locationPurpose: StockLocationPurpose.RECEIVING,
  },
  {
    workstationType: WorkstationType.MANUAL_RECEIVING,
    locationPurpose: StockLocationPurpose.RECEIVING,
  },
  {
    workstationType: WorkstationType.FIXED_RECEIVING,
    locationPurpose: StockLocationPurpose.RECEIVING,
  },
  {
    workstationType: WorkstationType.FIXED_PICKING,
    locationPurpose: StockLocationPurpose.PICKING,
  },
  {
    workstationType: WorkstationType.MOBILE_PICKING,
    locationPurpose: StockLocationPurpose.PICKING,
  },
  {
    workstationType: WorkstationType.HANDLING,
    locationPurpose: StockLocationPurpose.SPLITTING,
  },
  {
    workstationType: WorkstationType.HANDLING,
    locationPurpose: StockLocationPurpose.SPLICING,
  },
  {
    workstationType: WorkstationType.HANDLING,
    locationPurpose: StockLocationPurpose.REELING,
  },
  {
    workstationType: WorkstationType.PUTTING,
    locationPurpose: StockLocationPurpose.PUTTING,
  },
  {
    workstationType: WorkstationType.SHIPPING,
    locationPurpose: StockLocationPurpose.SHIPPING,
  },
  {
    workstationType: WorkstationType.COUNTING,
    locationPurpose: StockLocationPurpose.COUNTING,
  },
  {
    workstationType: WorkstationType.BAKING,
    locationPurpose: StockLocationPurpose.BAKING,
  },
  {
    workstationType: WorkstationType.INSPECTION,
    locationPurpose: StockLocationPurpose.INSPECTION,
  },
];

export const workstationTypeSpecifications = {
  [WorkstationType.PRE_RECEIVING]: {
    printerSizes: [PrinterLabelSize.THERMAL_0201],
    routes: [MANAGED_INVENTORY_ROUTES.PRE_RECEIVE],
    requiresLocalPrinting: true,
  },
  [WorkstationType.FIXED_RECEIVING]: {
    printerSizes: [PrinterLabelSize.THERMAL_0201],
    routes: [MANAGED_INVENTORY_ROUTES.FIXED_RECEIVE],
    requiresLocalPrinting: true,
  },
  [WorkstationType.INLINE_RECEIVING]: {
    printerSizes: [],
    routes: [MANAGED_INVENTORY_ROUTES.INLINE_RECEIVE],
  },
  [WorkstationType.MANUAL_RECEIVING]: {
    printerSizes: [PrinterLabelSize.THERMAL_0201],
    routes: [MANAGED_INVENTORY_ROUTES.MANUAL_RECEIVE],
  },
  [WorkstationType.FIXED_PICKING]: {
    printerSizes: [],
    routes: [MANAGED_INVENTORY_ROUTES.FIXED_PICKING],
  },
  [WorkstationType.MOBILE_PICKING]: {
    printerSizes: [],
    routes: [MANAGED_INVENTORY_ROUTES.MOBILE_PICKING],
  },
  [WorkstationType.HANDLING]: {
    printerSizes: [PrinterLabelSize.THERMAL_0201],
    routes: [MANAGED_INVENTORY_ROUTES.KITTING],
  },
  [WorkstationType.SHIPPING]: {
    printerSizes: [
      PrinterLabelSize.THERMAL_0302,
      PrinterLabelSize.THERMAL_0406,
      PrinterLabelSize.DOCUMENT_8511,
    ],
    routes: [MANAGED_INVENTORY_ROUTES.SHIPPING],
  },
  [WorkstationType.PUTTING]: {
    printerSizes: [PrinterLabelSize.THERMAL_0201],
    routes: [],
  },
  [WorkstationType.COUNTING]: {
    printerSizes: [],
    routes: [MANAGED_INVENTORY_ROUTES.COUNTER],
  },
  [WorkstationType.BAKING]: {
    printerSizes: [PrinterLabelSize.THERMAL_0201],
    routes: [MANAGED_INVENTORY_ROUTES.KITTING],
  },
  [WorkstationType.INSPECTION]: {
    printerSizes: [PrinterLabelSize.THERMAL_0201],
    routes: [MANAGED_INVENTORY_ROUTES.KITTING],
  },
  [WorkstationType.LEGACY]: {
    printerSizes: [PrinterLabelSize.THERMAL_0302],
    routes: [
      MANAGED_INVENTORY_ROUTES.DASHBOARD,
      MANAGED_INVENTORY_ROUTES.TRANSFER,
      MANAGED_INVENTORY_ROUTES.RECEIVE,
      MANAGED_INVENTORY_ROUTES.PICKING,
      MANAGED_INVENTORY_ROUTES.KITTING,
      MANAGED_INVENTORY_ROUTES.SHIPPING,
      MANAGED_INVENTORY_ROUTES.KIT_DASHBOARD,
    ],
  },
};

export const binPurposeIconMap = {
  [StockLocationPurpose.STORAGE]: IconArchive,
  [StockLocationPurpose.COUNTING]: IconTallymarks,
  [StockLocationPurpose.SHIPPING]: IconPlaneDeparture,
  [StockLocationPurpose.SPLITTING]: IconCut,
  [StockLocationPurpose.SPLICING]: IconArrowsJoin,
  [StockLocationPurpose.REELING]: IconDisc,
  [StockLocationPurpose.BAKING]: IconDropletOff,
  [StockLocationPurpose.RECEIVING]: IconTruckDelivery,
  [StockLocationPurpose.INSPECTION]: IconMicroscope,
  [StockLocationPurpose.PICKING]: IconBasket,
  [StockLocationPurpose.PUTTING]: IconArrowUp,
  [StockLocationPurpose.QUARANTINE]: IconAlertTriangle,
} as const;

export type StockLocationSchema = {
  id: string;
  name: string;
  org: Org;
  externalName: string | null;
  billable: boolean;
  active: boolean;
  customerLocation: boolean;
  manufacturer: boolean;
  requiresMslReset: boolean;
  requiresLcrInspection: boolean;
  requiresDimensionalInspection: boolean;
  acceptsTubes: boolean;
  acceptsSmtNoPnp: boolean;
  requiresReels: boolean;
  requiresMerges: boolean;
  requiresSplits: boolean;
  consumesStock: boolean;
  longTermStorage: boolean;
  stockLocationCode: string;
  notificationEmail: string | null;
  lockedAt: DateTimeString | null;
  locked: boolean;
  warehouseShippingEnabled: boolean;
  warehouseHandlingEnabled: boolean;
  address: OrgAddressBase;
  locationType: StockLocationType;
  defaultPackage: PackagingOptions | null;
  team: TeamLite | null;
  shipment: ShipmentSchemaLite | null;
  kitRequest: KitRequestSchemaLite | null;
  parent: StockLocationSchemaLite | null;
  facility: StockLocationSchemaLite;
  partRuleSet: PartRuleSet['id'] | null;
  smtNoPnpPartRuleSet: PartRuleSet['id'] | null;
  createdAt: DateTimeString;
  updatedAt: DateTimeString;
  internalMetadata: object | null;
  externalMetadata: object | null;
  customProperties: object | null;
  calledFor: boolean;
  purpose: StockLocationPurpose | null;
  useTeams: boolean;
};

// Note address is always expanded in the lite version
export type StockLocationSchemaLite = Lite<StockLocationSchema, 'address'>;

export type StockLocationPATCHSchema = PATCHSchema<{
  id: StockLocationSchema['id'];
  org: Org['id'];
  parent: StockLocationSchema['id'];
  shipment: ShipmentSchema['id'] | null;
  kitRequest: KitRequestSchema['id'] | null;
  purpose: StockLocationPurpose | null;
  calledFor: boolean;
}>;

export type StockLocationAssignSchema = POSTSchema<
  StockLocationSchema,
  'purpose',
  'shipment' | 'kitRequest' | 'org' | 'team'
>;
